import type { ActionType, Approval, FirebaseTransaction } from '../webapp/src/types';

export const isKfhtradeSecurityTransaction = (transaction: Pick<FirebaseTransaction, 'source' | 'transactionType'>) =>
    transaction.source === 'kfhtrade' && ['security_in', 'security_out'].includes(transaction.transactionType);

/** Only approvals in the current cycle count; a rejection invalidates older approvals. */
export function transactionApprovalStage(
    transaction: Pick<FirebaseTransaction, 'source' | 'transactionType'>,
    approvals: Approval[],
): 'rejected' | 'approval' | 'signatory' | 'operations' | 'done' {
    if (approvals[approvals.length - 1]?.type === 'reject') return 'rejected';
    const lastReject = Math.max(
        approvals.map(approval => approval.type).lastIndexOf('reject'),
        approvals.map(approval => approval.type).lastIndexOf('reject_signature'),
    );
    const active = approvals.slice(lastReject + 1);
    // Older portfolio approvals did not require an actor id to complete the request.
    if (transaction.source !== 'kfhtrade') return active.some(approval => approval.type === 'approval') ? 'done' : 'approval';
    const has = (type: Approval['type']) => active.some(approval => approval.type === type && !!approval.id);
    const signatory = isKfhtradeSecurityTransaction(transaction);
    if (signatory) {
        const crIndex = active.findIndex(approval => approval.type === 'approval' && !!approval.id);
        const signatoryIndex = active.findIndex((approval, index) => index > crIndex && approval.type === 'authorized_signatory_approve' && !!approval.id);
        const executed = active.some((approval, index) => index > signatoryIndex && approval.type === 'execution' && !!approval.id);
        // Transfers completed under the previous signatory -> Operations flow stay done.
        if (crIndex < 0) return signatoryIndex >= 0 && executed ? 'done' : 'approval';
        if (signatoryIndex < 0) return 'signatory';
        return executed ? 'done' : 'operations';
    }
    if (!has('approval')) return 'approval';
    return has('execution') ? 'done' : 'operations';
}

/** The callable checks this against the current approval trail inside its write transaction. */
export function transactionApprovalActionError(transaction: Pick<FirebaseTransaction, 'source' | 'transactionType'>, approvals: Approval[], action: ActionType): string | undefined {
    const stage = transactionApprovalStage(transaction, approvals);
    const expected = { approval: 'approval', authorized_signatory_approve: 'signatory', execution: 'operations' };
    if (action === 'approval' || action === 'authorized_signatory_approve' || action === 'execution') {
        if (stage !== expected[action]) return `Transaction is not waiting for ${action === 'approval' ? 'CR approval' : action === 'authorized_signatory_approve' ? 'executive/signatory approval' : 'Operations approval'}`;
    } else if (action === 'reject' && (stage === 'done' || stage === 'rejected')) {
        return 'Only a pending transaction can be rejected';
    }
}
